import { useMemo, useState } from "react";
import { countsOf, editsCatalog, editsMenus, partMenu } from "@speisekarte/core";
import type { Menu } from "@speisekarte/core";
import { api, dishesById, messageOf, store, useStore } from "../store.ts";
import type { Data } from "../store.ts";
import { downloadPdf, printMenu, printMenuOf } from "../output.ts";
import { outsideAll } from "../outside.ts";
import { OutsideFix, OutsideTags } from "./Outside.tsx";
import { TodayTile } from "./Plan.tsx";
import { Toast, useToast } from "./Toast.tsx";
import { EmailReminder } from "./Email.tsx";
import { Entrance } from "./Web.tsx";

const when = (at: number) => new Date(at).toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" });

/** „Karten“: the kept menus, newest first, and a new one in one tap (docs/01-architektur.md, Karte erstellen); below them the price list at the entrance (S42, here since S59: it is paper). */
export function Menus() {
  const s = useStore();
  const data = s.data!;
  const dishes = dishesById(data);
  const toast = useToast();
  const canEdit = editsMenus(s.role);
  // The menu whose ⚠ was tapped (S62, Z-0): the way to renew what outside shows of it.
  const [fixing, setFixing] = useState<string | null>(null);
  const fix = fixing ? data.menus.find((m) => m.id === fixing) : null;
  const outside = useMemo(() => outsideAll(data), [data]);

  async function remove(m: Menu) {
    if (!confirm(`Speisekarte „${m.title}“ löschen?`)) return;
    try {
      await store.change(() => api(`/api/menus/${m.id}`, { method: "DELETE" }));
    } catch (err) {
      toast.show(messageOf(err));
    }
  }

  return (
    <div className="menus">
      <EmailReminder />
      {/* Heute (S62, Bild 12): only with „Erweitert“ and a weekly plan. */}
      <TodayTile data={data} />
      {/* A plain page the server delivers (public/tutorials.html), in a new window like the imprint. */}
      <p className="tutorials-link">
        <a href="/tutorials" target="_blank" rel="noopener" data-testid="tutorials-link">
          Tutorials ↗
        </a>
      </p>
      {canEdit && (
        <section className="card">
          <h2>Neue Speisekarte</h2>
          <div className="new">
            <button className="primary" onClick={() => (location.hash = "#/speisekarte/neu?form=list")} data-testid="new-list">
              <b>Liste</b>
              <span className="small">Speisen in einer Reihe, etwa Tapas</span>
            </button>
            <button onClick={() => (location.hash = "#/speisekarte/neu?form=sections")} data-testid="new-sections">
              <b>Gegliedert</b>
              <span className="small">Vorspeisen, Hauptspeisen, Desserts, Menüs</span>
            </button>
          </div>
          {data.dishes.length === 0 && (
            <p className="hint">
              Noch keine Speisen im Katalog. <a href="#/kataloge">Speisen anlegen</a> oder die alte Karte oder eine Excel-Mappe übernehmen: <a href="#/import">Excel und Einlesen …</a>
            </p>
          )}
        </section>
      )}

      <h2>Gespeicherte Speisekarten</h2>
      {data.menus.length === 0 && <p className="muted">Noch keine Speisekarte gespeichert.</p>}
      <ul className="list card" style={{ padding: "0 0.8rem" }}>
        {data.menus.map((m) => (
          <li key={m.id} className="menu-row" data-testid="menu-row">
            <a href={`#/speisekarte/${m.id}`}>
              <strong>{m.title}</strong>
              <OutsideTags uses={outside.get(m.id) ?? []} onOld={() => setFixing(m.id)} />
              <br />
              <span className="muted" data-testid="menu-facts">
                {facts(m, dishes).map((f, i) => (
                  <span key={f}>
                    {i > 0 && " · "}
                    <span className="nowrap">{f}</span>
                  </span>
                ))}
                <br />
                <span className="nowrap">geändert {when(m.updatedAt)}</span>
              </span>
            </a>
            <span className="actions">
              <button onClick={() => void printMenu(printMenuOf(m, data), data).catch((e) => toast.show(messageOf(e)))} title="Drucken">
                Drucken
              </button>
              <button onClick={() => void downloadPdf(printMenuOf(m, data), data).catch((e) => toast.show(messageOf(e)))} title="Als PDF herunterladen">
                PDF
              </button>
              {canEdit && (
                <button onClick={() => (location.hash = `#/speisekarte/neu?von=${m.id}`)} title="Als Vorlage für eine neue Speisekarte">
                  Kopie
                </button>
              )}
              {canEdit && (outside.get(m.id) ?? []).length === 0 && (
                <button className="danger" onClick={() => void remove(m)} aria-label={`${m.title} löschen`}>
                  ✕
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      <p className="hint">Gespeichert bleiben die letzten {data.settings.keepMenus} Speisekarten sowie alle, die gerade im Netz stehen oder am Eingang aushängen.</p>
      {data.settings.entranceMenu && <Entrance data={data} />}
      {fix && <OutsideFix menu={fix} data={data} title={fix.title} onClose={() => setFixing(null)} />}
      <Toast toast={toast} />
    </div>
  );
}

/**
 * What a menu has, each only when there is some (Martin, 25.10.2026): „Liste · 7 Speisen · 2 Texte“,
 * „3 Abschnitte · 9 Speisen · 27 Getränke“. The build is the food's.
 */
function facts(m: Menu, dishes: ReturnType<typeof dishesById>): string[] {
  const food = partMenu(m, "food");
  const n = countsOf(m, dishes);
  const count = (k: number, one: string, many: string) => (k ? `${k} ${k === 1 ? one : many}` : "");
  return [
    food.structure === "list" ? "Liste" : count(food.sections.length, "Abschnitt", "Abschnitte"),
    count(n.food, "Speise", "Speisen"),
    count(n.drinks, "Getränk", "Getränke"),
    count(n.texts, "Textbaustein", "Textbausteine"),
  ].filter(Boolean);
}
